import React, { useState, useEffect, useRef } from "react";
import * as Mui from "@mui/material";
import DefaultMonthDateRangePicker from "pages/TeamZone/Approve/DateRangePicker";
import { KeyboardArrowDown, KeyboardArrowRight } from "@mui/icons-material";
import { InfoMessage } from "components/CustomComponents/CustomButton";
import { Loader } from "components/Loader";
import { themes, scrollTop, tableRowSx } from "services/constants";
import { useSelector } from "react-redux";
import { showToast } from "services/utils/Status";
import sessionHandling from "services/utils/notificationUtils";
import { IncentiveHistoryHeader } from "../Headers";

export const History = () => {
  const { domain, token, userDetails, globalSearchValue } = useSelector(
    (state) => state.tokenReducer
  );
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [expandedRow, setExpandedRow] = React.useState(null);
  const [filteredData, setFilteredData] = useState([]);
  const [incentiveHistoryDetails, setIncentiveHistoryDetails] = useState([]);
  const containerRef = useRef(null);

  const handleRowClick = (rowId) => {
    if (expandedRow === rowId) {
      setExpandedRow(null);
    } else {
      setExpandedRow(rowId);
    }
  };

  const handleStartDate = async (startDate, endDate) => {
    let searchStartDate = startDate.split("/").reverse().join("-");
    let searchEndDate = endDate.split("/").reverse().join("-");
    setLoading(true);
    try {
      const response = await fetch(
        `${domain}my-incentive-applications/?user_id=${userDetails.id}&status=History&start_date=${searchStartDate}&end_date=${searchEndDate}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `token ${token}`,
          },
        }
      );
      const res = await response.json();
      if (response.ok) {
        setFilteredData(res);
        setIncentiveHistoryDetails(res);
      } else if (response.status === 409) {
        sessionHandling();
      } else {
        throw new Error(res.error);
      }
      setLoading(false);
    } catch (error) {
      showToast("error", error.message);
    }
  };

  useEffect(() => {
    const searchFields = [
      "incentive_type",
      "incentive_group_name",
      "start_date",
      "end_date",
      "incentive_amount",
      "status",
      "payrun_status",
      "payrun_month",
    ];
    const filteredResults = incentiveHistoryDetails.filter((item) =>
      searchFields.some((key) =>
        item[key]
          ?.toString()
          .toLowerCase()
          .includes(globalSearchValue.toString().toLowerCase())
      )
    );
    setFilteredData(filteredResults);
    setPage(0);
  }, [globalSearchValue, incentiveHistoryDetails]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  useEffect(() => {
    scrollTop(containerRef);
  }, [page]);

  return (
    <React.Fragment>
      <Mui.Grid container>
        <Mui.Grid item xs={9.25}></Mui.Grid>
        <Mui.Grid item xs={2.6} sx={{ paddingTop: 1, marginTop: -7 }}>
          <DefaultMonthDateRangePicker
            handleStartDate={handleStartDate}
            showToday={true}
            triggerDate={true}
          />
        </Mui.Grid>
      </Mui.Grid>

      {loading === true ? (
        <Loader info="Loading..." />
      ) : (
        <>
          <Mui.Grid container>
            <Mui.TableContainer
              sx={{ width: "100%", maxHeight: 700, mt: 2, ml: 2, mr: 2 }}
              ref={containerRef}
            >
              <Mui.Table className="border">
                <Mui.TableHead
                  sx={{
                    backgroundColor: themes.primary,
                    position: "sticky",
                    top: -1,
                    zIndex: 10,
                  }}
                >
                  <Mui.TableRow sx={tableRowSx}>
                    {IncentiveHistoryHeader.map((header) => (
                      <Mui.TableCell
                        sx={{ color: themes.headLine, fontWeight: "bold" }}
                      >
                        {header.name}
                      </Mui.TableCell>
                    ))}
                  </Mui.TableRow>
                </Mui.TableHead>

                <Mui.TableBody>
                  {filteredData?.length === 0 ? (
                    <InfoMessage dataLength={incentiveHistoryDetails} />
                  ) : (
                    filteredData
                      .slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage
                      )
                      .map((item) => (
                        <>
                          <Mui.TableRow sx={tableRowSx}>
                            {IncentiveHistoryHeader.map((header) => (
                              <Mui.TableCell
                                sx={{
                                  color: themes.headLine,
                                  width:
                                    header.key === "arrow"
                                      ? "1%"
                                      : header.key === "incentive_group_name"
                                      ? "15%"
                                      : "10%",
                                }}
                              >
                                {header.key === "arrow" ? (
                                  <>
                                    {expandedRow === item.id ? (
                                      <Mui.IconButton
                                        onClick={() => {
                                          setExpandedRow(null);
                                        }}
                                      >
                                        <KeyboardArrowDown
                                          style={{
                                            color: "black",
                                            fontWeight: "bold",
                                          }}
                                        />
                                      </Mui.IconButton>
                                    ) : (
                                      <Mui.IconButton
                                        onClick={() => handleRowClick(item.id)}
                                      >
                                        <KeyboardArrowRight
                                          style={{
                                            color: "black",
                                            fontWeight: "bold",
                                          }}
                                        />
                                      </Mui.IconButton>
                                    )}
                                  </>
                                ) : item[header.key] ? (
                                  item[header.key]
                                ) : (
                                  "N/A"
                                )}
                              </Mui.TableCell>
                            ))}
                          </Mui.TableRow>
                          {expandedRow === item.id && (
                            <Mui.TableRow>
                              <Mui.TableCell colSpan={10}>
                                <Mui.TableContainer
                                  sx={{
                                    marginTop: 0.5,
                                    marginLeft: "auto",
                                    marginRight: "auto",
                                    width: "100%px",
                                  }}
                                  className="w-96 border rounded"
                                >
                                  <Mui.Table className="border">
                                    <Mui.Grid sx={{ padding: 0.5 }}>
                                      <Mui.Grid
                                        container
                                        sx={{ paddingTop: 1, paddingLeft: 2 }}
                                      >
                                        <Mui.Stack direction="row">
                                          <Mui.Typography
                                            sx={{ fontWeight: "bold" }}
                                          >
                                            Applied by
                                          </Mui.Typography>
                                          <Mui.Typography
                                            sx={{ paddingLeft: 1 }}
                                          >
                                            {item?.appliedby
                                              ? item?.appliedby
                                              : "-"}
                                          </Mui.Typography>

                                          <Mui.Typography
                                            sx={{
                                              fontWeight: "bold",
                                              paddingLeft: 1,
                                            }}
                                          >
                                            on
                                          </Mui.Typography>

                                          <Mui.Typography
                                            sx={{ paddingLeft: 1 }}
                                          >
                                            {item?.appliedat
                                              ? item?.appliedat
                                              : "-"}
                                          </Mui.Typography>
                                        </Mui.Stack>
                                      </Mui.Grid>
                                      {item?.status !== "Rejected" && (
                                        <Mui.Grid
                                          container
                                          sx={{ paddingTop: 1, paddingLeft: 2 }}
                                        >
                                          <Mui.Stack direction="row">
                                            <Mui.Typography
                                              sx={{ fontWeight: "bold" }}
                                            >
                                              {item?.status === "Cancelled"
                                                ? "Cancelled by"
                                                : "Approved by"}
                                            </Mui.Typography>
                                            <Mui.Typography
                                              sx={{ paddingLeft: 1 }}
                                            >
                                              {item?.status === "Approved"
                                                ? item?.approvedby || "-"
                                                : item?.status === "Cancelled"
                                                ? item?.cancelledby || "-"
                                                : "-"}
                                            </Mui.Typography>
                                            <Mui.Typography
                                              sx={{
                                                fontWeight: "bold",
                                                paddingLeft: 1,
                                              }}
                                            >
                                              on
                                            </Mui.Typography>
                                            <Mui.Typography
                                              sx={{ paddingLeft: 1 }}
                                            >
                                              {item?.status === "Approved"
                                                ? item?.approvedat || "-"
                                                : item?.status === "Cancelled"
                                                ? item?.cancelledat || "-"
                                                : "-"}
                                            </Mui.Typography>
                                          </Mui.Stack>
                                        </Mui.Grid>
                                      )}
                                      {item?.status === "Rejected" && (
                                        <Mui.Grid
                                          container
                                          sx={{ paddingTop: 1, paddingLeft: 2 }}
                                        >
                                          <Mui.Stack direction="row">
                                            <Mui.Typography
                                              sx={{ fontWeight: "bold" }}
                                            >
                                              Rejected by
                                            </Mui.Typography>
                                            <Mui.Typography
                                              sx={{ paddingLeft: 1 }}
                                            >
                                              {item?.rejectedby
                                                ? item?.rejectedby
                                                : "-"}
                                            </Mui.Typography>
                                            <Mui.Typography
                                              sx={{
                                                fontWeight: "bold",
                                                paddingLeft: 1,
                                              }}
                                            >
                                              on
                                            </Mui.Typography>
                                            <Mui.Typography
                                              sx={{ paddingLeft: 1 }}
                                            >
                                              {item?.rejectedat
                                                ? item?.rejectedat
                                                : "-"}
                                            </Mui.Typography>
                                          </Mui.Stack>
                                        </Mui.Grid>
                                      )}
                                      <Mui.Grid
                                        container
                                        sx={{ paddingTop: 1, paddingLeft: 2 }}
                                      >
                                        <Mui.Stack
                                          direction="row"
                                          flexWrap="wrap"
                                        >
                                          <Mui.Typography
                                            sx={{
                                              fontWeight: "bold",
                                            }}
                                          >
                                            Description :
                                          </Mui.Typography>
                                          <Mui.Typography
                                            sx={{
                                              paddingLeft: 1,
                                              whiteSpace: "normal",
                                              wordBreak: "break-word",
                                              flex: "1 1 0",
                                            }}
                                          >
                                            {item.description ?? "N/A"}
                                          </Mui.Typography>
                                        </Mui.Stack>
                                      </Mui.Grid>
                                    </Mui.Grid>
                                  </Mui.Table>
                                </Mui.TableContainer>
                              </Mui.TableCell>
                            </Mui.TableRow>
                          )}
                        </>
                      ))
                  )}
                </Mui.TableBody>
              </Mui.Table>
            </Mui.TableContainer>
          </Mui.Grid>
          {filteredData.length > 25 && (
            <Mui.TablePagination
              className="custom-pagination"
              rowsPerPageOptions={[25, 50, 75, 100]}
              component="div"
              count={filteredData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </>
      )}
    </React.Fragment>
  );
};
